import React, { useCallback, useState } from "react";

import RCUpload from "rc-upload";

import { Typography } from "@mui/material";
import { RcFile, UploadRequestOption } from "rc-upload/lib/interface";
import { useTranslation } from "react-i18next";
import { Box, FlexColBox, Icon } from "../../../common/components";
import { Button } from "../../../common/components/Button/Button";
import { palette, spacing } from "../../../styles/theme";
import { DesignDecoration } from "../../../types";
import Message from "../../Message/Message";
import styles from "./UploadImage.module.scss";
import { useStudio } from "../../../context/Studio";

interface Props {
  onStart?: (file: File) => Promise<void> | void;
  onUpload: (options: UploadRequestOption) => Promise<void> | void;
  onSuccess?: (res: any, file: File) => Promise<void>;
  errorCode?: string | null;
  title?: string;
  acceptedFilesTitle: string;
  acceptedFileExtensions?: string[];
  assetName?: string;
  onAssetResetChange?: (decoration: DesignDecoration) => void;
  decoration?: DesignDecoration;
  loadingFileName?: string;
  size?: "small" | "medium";
}
const UploadImageButton = (props: Props) => {
  const { t, i18n } = useTranslation();
  const {
    onStart,
    onUpload,
    onSuccess,
    errorCode,
    title = t("general.upload_file"),
    acceptedFilesTitle,
    acceptedFileExtensions = [".eps, .svg"],
    assetName,
    onAssetResetChange,
    decoration,
    size = "medium",
  } = props;

  const { isDesignLocked } = useStudio();

  //TODO, we need another state also that said "Upload successfull", instead of just putting back the button
  //Or make the loading state a string with various enums...
  const [loading, setLoading] = useState(false);

  const customRequest = useCallback(
    (options: any) => {
      onUpload(options);
    },
    [onUpload]
  );

  const acceptedFileExtensionsString = acceptedFileExtensions.join(", ");
  const rcUploadProps = {
    multiple: false,
    accept: acceptedFileExtensionsString,
    onStart: async (file: RcFile) => {
      setLoading(true);

      if (onStart) {
        await onStart(file);
      }
    },
    onSuccess: async (res: any, file: File) => {
      if (onSuccess) {
        await onSuccess(res, file);
      }
      setLoading(false);
    },
    onError: () => {
      setLoading(false);
    },
    customRequest: customRequest,
    component: "div",
    className: styles.rcUpload,
  };

  return (
    <React.Fragment>
      {loading ? (
        <span>{t("general.loading")}</span>
      ) : (
        <FlexColBox gap={spacing.mediumDesktop}>
          {acceptedFilesTitle && (
            <Typography variant="textSm" color={palette.grey.primary}>
              {acceptedFilesTitle}
            </Typography>
          )}

          {errorCode && (
            <Message
              title={t("assetManager.errors." + errorCode + ".title")}
              message={t("assetManager.errors." + errorCode + ".message")}
              linkText={
                i18n.exists("assetManager.errors." + errorCode + ".linkText")
                  ? t("assetManager.errors." + errorCode + ".linkText")
                  : undefined
              }
              linkUrl={
                i18n.exists("assetManager.errors." + errorCode + ".linkUrl")
                  ? t("assetManager.errors." + errorCode + ".linkUrl")
                  : undefined
              }
            />
          )}

          {!assetName && !isDesignLocked && (
            <RCUpload {...rcUploadProps}>
              <Button fullWidth variant="outlined" size={size}>
                {title}
              </Button>
            </RCUpload>
          )}

          {assetName && (
            <Box
              sx={{
                p: "8px 8px 8px 12px",
                display: "flex",
                borderRadius: "8px",
                alignItems: "center",
                justifyContent: "space-between",
                border: `1px solid ${palette.grey.darkGrey}`,
              }}
            >
              <Typography variant="actionSmallIcon">{assetName}</Typography>
              {!isDesignLocked && (
                <Icon
                  size={24}
                  icon="close"
                  color="#777777"
                  onClick={() =>
                    onAssetResetChange &&
                    decoration &&
                    onAssetResetChange(decoration)
                  }
                />
              )}
            </Box>
          )}
        </FlexColBox>
      )}
    </React.Fragment>
  );
};

export default UploadImageButton;
